import { useHotkeys } from "react-hotkeys-hook";
import { CommandAction } from "../../lib/commandCore";
import { haptic } from "../../lib/haptics";
import { useUiStore } from "../../stores/uiStore";

interface Props {
  suggestedAction: CommandAction;
  onChoose: (action: CommandAction) => void;
  disabled?: boolean;
}

const actions: Array<{ key: CommandAction; label: string; mobileLabel: string; tone: string }> = [
  { key: "A", label: "調査", mobileLabel: "調", tone: "border-sky-200 text-sky-800 hover:bg-sky-50 dark:border-sky-400/35 dark:text-sky-100 dark:hover:bg-sky-400/15" },
  { key: "B", label: "案作成", mobileLabel: "案", tone: "border-indigo-200 text-indigo-800 hover:bg-indigo-50 dark:border-indigo-400/35 dark:text-indigo-100 dark:hover:bg-indigo-400/15" },
  { key: "C", label: "実行準備", mobileLabel: "準", tone: "border-emerald-200 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-400/35 dark:text-emerald-100 dark:hover:bg-emerald-400/15" },
  { key: "D", label: "保留", mobileLabel: "保", tone: "border-border text-muted hover:bg-hover dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-700/40" },
];

export function CommandBar({ suggestedAction, onChoose, disabled = false }: Props) {
  const modalOpen = useUiStore((s) => s.editorOpen || s.settingsOpen || s.shortcutsOpen || s.voiceOpen);
  const choose = (key: CommandAction) => {
    haptic(key === "C" ? "success" : "light");
    onChoose(key);
  };

  useHotkeys("a,b,c,d", (event) => {
    if (disabled || modalOpen) return;
    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName.toLowerCase();
    if (tag === "input" || tag === "textarea" || active?.isContentEditable) return;
    const key = event.key.toUpperCase() as CommandAction;
    if (actions.some((item) => item.key === key)) choose(key);
  });

  return (
    <div className="shrink-0 border-t border-border bg-card/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 text-text backdrop-blur dark:border-white/10 dark:bg-[#111113]/95 dark:text-white md:px-8 md:pb-3">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="command-muted text-xs font-semibold">次の一手</p>
          <p className="font-mono text-lg font-bold text-text dark:text-white">GO {suggestedAction}</p>
        </div>
        <div className="grid flex-1 grid-cols-4 gap-2 sm:flex sm:flex-none">
          {actions.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => choose(item.key)}
              aria-label={`GO ${item.key} ${item.label}`}
              className={`flex min-h-12 min-w-0 flex-col items-center justify-center rounded-md border bg-bg px-2 py-2 text-center transition dark:bg-black/20 sm:min-w-24 sm:flex-row sm:justify-start sm:px-3 sm:text-left ${item.tone} ${
                item.key === suggestedAction ? "ring-2 ring-brand-300 dark:ring-white/30" : ""
              }`}
            >
              <span className="font-mono text-lg font-bold">{item.key}</span>
              <span className="text-[10px] font-semibold leading-none sm:hidden">{item.mobileLabel}</span>
              <span className="ml-2 hidden whitespace-nowrap text-sm font-semibold sm:inline">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
